/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@pinia/colada';
import ConflictSearchService from '../services/ConflictSearchService';
import ConflictCheckRecordService from '../services/ConflictCheckRecordService';
import {
  CONFLICT_BILLING_INSTRUCTIONS_FIELD,
  CONFLICT_SEARCHED_FIELDS,
} from '../constants/conflictFieldConstants';
import { withoutGatedRows } from '../utils/conflictPermissionHelpers';
import type {
  ConflictCheckRecordDetail,
  ConflictCheckResponse,
  ConflictPreviewRecord,
  ConflictResultRow,
} from '../models/conflictTypes';

/**
 * @pinia/colada is an internal request wrapper here — no component imports it. The
 * store's refs are the public contract.
 *
 * The scan is `staleTime: 0` and driven through `.refetch(true)`. A conflict check is
 * a point-in-time read of firm data that someone is about to rely on; serving it from
 * cache would answer a question the reviewer did not ask. `throwOnError` matters more
 * here than anywhere else in the app: Colada's default resolves a failed fetch with
 * the entry's last-known state, which would let a failed check render as the previous
 * check's results, or as a clean one. That is the single worst output this feature can
 * produce, so the failure has to reach the catch below.
 *
 * Query and time-entry scope are in the key, not only in the refs, so a background
 * refetch cannot write one query's response into another's entry.
 */
export const useConflictCheckStore = defineStore('conflictCheck', () => {
  const query = ref('');
  const includeTimeEntries = ref(false);

  const result = ref<ConflictCheckResponse | null>(null);
  const running = ref(false);
  const error = ref<unknown>(null);
  /** What the last completed run actually covered — not what the checkbox says now. */
  const ranWithTimeEntries = ref(false);
  const ranQuery = ref('');
  /** The client's clock at completion — the time a printed, unsaved sheet carries. */
  const ranAt = ref<string | null>(null);

  /**
   * The reviewer's notes on the check on screen. Kept across a re-run: refining the
   * query does not make what they have written so far wrong, and discarding it would
   * be the silent loss EC-003 rules out.
   */
  const notes = ref('');
  const saving = ref(false);
  const saveError = ref<unknown>(null);

  /** Client-side only: fields the reviewer has hidden from the returned results. */
  const hiddenFields = ref<string[]>([]);

  const preview = ref<ConflictPreviewRecord | null>(null);
  const previewLoading = ref(false);
  const previewError = ref<unknown>(null);
  const previewTarget = ref<{ id: string; type: string } | null>(null);

  /** Set by the page from me.permissions. Defaults closed. */
  const canSeeBillingInstructions = ref(false);

  const hasRun = computed(() => result.value !== null);
  const failed = computed(() => error.value !== null);
  /** What leaving the check would lose. Results alone can be re-run; notes cannot. */
  const hasUnsavedNotes = computed(() => notes.value.trim() !== '');

  /**
   * The permission gate sits here rather than at each render site, so the rows, the
   * counts, the filter chips, the preview and the printed sheet all inherit it.
   */
  const permitted = (rows: ConflictResultRow[]): ConflictResultRow[] =>
    withoutGatedRows(rows, canSeeBillingInstructions.value);

  const allRows = computed<ConflictResultRow[]>(() =>
    result.value
      ? permitted([...result.value.fullMatches, ...result.value.partialMatches])
      : [],
  );

  /** Fields the filter may offer — a gated field is not merely empty, it is absent. */
  const permittedFields = computed(() =>
    canSeeBillingInstructions.value
      ? CONFLICT_SEARCHED_FIELDS
      : CONFLICT_SEARCHED_FIELDS.filter(
          (field) => field !== CONFLICT_BILLING_INSTRUCTIONS_FIELD,
        ),
  );

  /**
   * A row survives the filter while any one of its fields is still selected, and shows
   * only those — a hidden field's pill and highlighted value go with it. Counts read
   * allRows instead, so a hidden field's pill in the filter still says how many it has.
   */
  const visibleOnly = (rows: ConflictResultRow[]): ConflictResultRow[] =>
    rows
      .map((row) => ({
        ...row,
        fields: row.fields.filter(
          (field) => hiddenFields.value.indexOf(field.id) === -1,
        ),
      }))
      .filter((row) => row.fields.length > 0);

  const visibleFullMatches = computed(() =>
    visibleOnly(permitted(result.value?.fullMatches ?? [])),
  );

  const visiblePartialMatches = computed(() =>
    visibleOnly(permitted(result.value?.partialMatches ?? [])),
  );

  const visibleRowCount = computed(
    () => visibleFullMatches.value.length + visiblePartialMatches.value.length,
  );

  const hiddenRowCount = computed(
    () => allRows.value.length - visibleRowCount.value,
  );

  /** Result count per field across both tiers, for the filter pills. */
  const fieldCounts = computed<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    allRows.value.forEach((row) => {
      row.fields.forEach((field) => {
        counts[field.id] = (counts[field.id] ?? 0) + 1;
      });
    });
    return counts;
  });

  const searchableParties = computed(
    () => result.value?.parties.filter((party) => party.searchable) ?? [],
  );

  /** The rows the preview highlights against — a saved check supplies its own. */
  const previewRows = ref<ConflictResultRow[]>([]);

  let previewToken = 0;

  function closePreview(): void {
    previewToken += 1;
    previewTarget.value = null;
    previewRows.value = [];
    preview.value = null;
    previewError.value = null;
    previewLoading.value = false;
  }

  function visibleRowsInclude(recordId: string): boolean {
    return [...visibleFullMatches.value, ...visiblePartialMatches.value].some(
      (row) => row.recordId === recordId,
    );
  }

  const scanQuery = useQuery({
    key: () => [
      'conflict-check',
      query.value,
      String(includeTimeEntries.value),
    ],
    query: () =>
      ConflictSearchService.runCheck(query.value, includeTimeEntries.value),
    enabled: false,
    staleTime: 0,
  });

  let runToken = 0;

  async function runCheck(): Promise<void> {
    const token = ++runToken;
    running.value = true;
    error.value = null;
    saveError.value = null;
    // Clear the previous result before the new one lands. A stale result sitting under
    // a spinner reads as the answer to the query now in the box.
    result.value = null;
    hiddenFields.value = [];
    closePreview();
    try {
      const state = await scanQuery.refetch(true);
      if (token !== runToken) return;
      if (!state.data) {
        throw new Error('The conflict check returned no response.');
      }
      result.value = state.data;
      ranWithTimeEntries.value = includeTimeEntries.value;
      ranQuery.value = query.value;
      ranAt.value = new Date().toISOString();
    } catch (e) {
      if (token !== runToken) return;
      result.value = null;
      error.value = e;
      throw e;
    } finally {
      if (token === runToken) running.value = false;
    }
  }

  const previewQuery = useQuery({
    key: () => [
      'conflict-preview',
      previewTarget.value?.type ?? '',
      previewTarget.value?.id ?? '',
    ],
    query: () => {
      const target = previewTarget.value;
      if (!target) return Promise.resolve(null);
      const rows = previewRows.value.filter(
        (row) => row.recordId === target.id,
      );
      if (target.type === 'client') {
        return ConflictSearchService.fetchClientPreview(target.id, rows);
      }
      if (target.type === 'matter') {
        return ConflictSearchService.fetchMatterPreview(
          target.id,
          rows,
          canSeeBillingInstructions.value,
        );
      }
      return ConflictSearchService.fetchEntryPreview(target.id, rows);
    },
    enabled: false,
    staleTime: 0,
  });

  async function openPreview(
    row: ConflictResultRow,
    rows: ConflictResultRow[] = allRows.value,
  ): Promise<void> {
    const token = ++previewToken;
    previewRows.value = rows;
    previewTarget.value = { id: row.recordId, type: row.recordType };
    previewLoading.value = true;
    previewError.value = null;
    preview.value = null;
    try {
      const state = await previewQuery.refetch(true);
      if (token !== previewToken) return;
      preview.value = state.data ?? null;
    } catch (e) {
      if (token !== previewToken) return;
      previewError.value = e;
    } finally {
      if (token === previewToken) previewLoading.value = false;
    }
  }

  function toggleField(field: string): void {
    const at = hiddenFields.value.indexOf(field);
    hiddenFields.value =
      at === -1
        ? hiddenFields.value.concat(field)
        : hiddenFields.value.filter((hidden) => hidden !== field);
    // A preview whose row is now filtered away has nothing on screen to have come from.
    if (preview.value && !visibleRowsInclude(preview.value.id)) closePreview();
  }

  function showAllFields(): void {
    hiddenFields.value = [];
  }

  const saveMutation = useMutation({
    mutation: () =>
      ConflictCheckRecordService.save(
        ranQuery.value,
        ranWithTimeEntries.value,
        notes.value,
      ),
  });

  /**
   * Saves what was run, not what is in the box now. The server runs the check again
   * and stores that; on failure the results and notes stay exactly as they were.
   */
  async function save(): Promise<ConflictCheckRecordDetail> {
    saving.value = true;
    saveError.value = null;
    try {
      return await saveMutation.mutateAsync();
    } catch (e) {
      saveError.value = e;
      throw e;
    } finally {
      saving.value = false;
    }
  }

  function setBillingInstructionsAccess(allowed: boolean): void {
    canSeeBillingInstructions.value = allowed;
  }

  function reset(): void {
    query.value = '';
    includeTimeEntries.value = false;
    result.value = null;
    error.value = null;
    hiddenFields.value = [];
    // ranQuery holds the parties verbatim — clear it with the rest on teardown.
    ranQuery.value = '';
    ranWithTimeEntries.value = false;
    ranAt.value = null;
    notes.value = '';
    saveError.value = null;
    closePreview();
  }

  return {
    query,
    includeTimeEntries,
    result,
    running,
    error,
    failed,
    hasRun,
    ranWithTimeEntries,
    ranQuery,
    ranAt,
    notes,
    saving,
    saveError,
    hasUnsavedNotes,
    canSeeBillingInstructions,
    hiddenFields,
    allRows,
    visibleFullMatches,
    visiblePartialMatches,
    visibleRowCount,
    hiddenRowCount,
    fieldCounts,
    permittedFields,
    searchableParties,
    preview,
    previewLoading,
    previewError,
    runCheck,
    save,
    openPreview,
    closePreview,
    toggleField,
    showAllFields,
    setBillingInstructionsAccess,
    reset,
  };
});
